/**
 * Places (#6559 round 17, Claude Design): where a reader can stand on the
 * Documents page — a drive, a connection's root, or the one tree of an
 * organization without drives. The column lists them; the title row names
 * the one the listing is in and says what it is; the trail starts at it and
 * never climbs above it.
 *
 * A connection's place is a view over the root folder its files already sit
 * under (in the drive they sit in), so a listing's address stays what it was:
 * a drive and a path. This module only decides which place that address is in.
 */
import type { PlaceApp, PlaceConnection, PlaceDrive, Places } from "./api";

export interface CurrentPlace {
  kind: "drive" | "connection" | "tree";
  /** What the heading says at the place's root. */
  label: string;
  /** What the column's row for the place says (round 19, Claude Design
   *  Q2): the label, except a connection, which the row names under its
   *  app when the app has several (`connectionDisplayName`). A Location
   *  cell and a trail's root crumb use this, so they always name a row
   *  that exists in the column. */
  name: string;
  /** What the place is, under the heading; null where nothing needs saying. */
  lede: string | null;
  driveId: string | null;
  /** The path the place starts at: [] for a drive or the tree, the
   *  connection's root folder for a connection. */
  root: string[];
  /** The connected app, for the mark beside a connection's name. */
  app: string | null;
}

/** The viewer's own drive reads as "My Drive" wherever it is named — the
 *  stored name ("Hunter's Drive") is what other people would call it. */
export const MY_DRIVE_LABEL = "My Drive";

export const APP_LABELS: Record<string, string> = {
  sharepoint: "SharePoint",
  onenote: "OneNote",
  teams: "Teams channels",
  box: "Box",
  dropbox: "Dropbox",
  google_drive: "Google Drive",
  dealcloud: "DealCloud",
  backstop: "Backstop",
  sftp: "File server",
};

export const appLabel = (app: string): string => APP_LABELS[app] ?? app;

/** How the column names a connection (the one rule, shared with
 *  `PlaceRows`): under its app, "SharePoint · Investments", only when the
 *  app has more than one connection; the app alone otherwise (a single
 *  SharePoint site, Teams channels). */
export const connectionDisplayName = (
  app: PlaceApp,
  connection: PlaceConnection
): string =>
  app.connections.length > 1 ? `${app.label} · ${connection.label}` : app.label;

const sameSegment = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase();

/** The connection whose root the path is in, in the given drive. A path is
 *  in a connection's place when its first segments are the connection's
 *  root; a connection with no drive matches in the drive-less tree. */
export const connectionAt = (
  places: Places | undefined,
  driveId: string | null,
  path: string[]
): { app: PlaceApp; connection: PlaceConnection } | null => {
  if (!places || path.length === 0) return null;
  for (const app of places.connectedApps) {
    for (const connection of app.connections) {
      if ((connection.driveId ?? null) !== (driveId ?? null)) continue;
      if (connection.path.length > path.length) continue;
      if (connection.path.every((seg, i) => sameSegment(seg, path[i] ?? "")))
        return { app, connection };
    }
  }
  return null;
};

/** How long ago an instant was, for the provenance line: coarse on purpose,
 *  since "synced 4 min ago" is a freshness cue rather than a timestamp. */
export const timeAgo = (iso: string, now: number = Date.now()): string => {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const minutes = Math.max(0, Math.round((now - then) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
};

/** The provenance line under a connection's heading: app · site · synced. */
export const connectionLede = (
  app: PlaceApp,
  connection: PlaceConnection,
  now: number = Date.now()
): string => {
  const parts: string[] = [app.label];
  if (connection.siteUrl) {
    parts.push(
      connection.siteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")
    );
  }
  if (connection.lastSync)
    parts.push(`synced ${timeAgo(connection.lastSync, now)}`);
  return parts.join(" · ");
};

/** The place a listing address is in. Without the places read the address
 *  is the tree, as before round 17. */
export const placeFor = (
  places: Places | undefined,
  driveId: string | null,
  path: string[]
): CurrentPlace => {
  const hit = connectionAt(places, driveId, path);
  if (hit) {
    return {
      kind: "connection",
      label: hit.connection.label,
      name: connectionDisplayName(hit.app, hit.connection),
      lede: connectionLede(hit.app, hit.connection),
      driveId: hit.connection.driveId,
      root: hit.connection.path,
      app: hit.app.app,
    };
  }
  if (places?.drivesEnabled && driveId) {
    if (places.myDrive && places.myDrive.driveId === driveId) {
      return {
        kind: "drive",
        label: MY_DRIVE_LABEL,
        name: MY_DRIVE_LABEL,
        // A drive carries no lede (round 18b, Hunter): the explanation
        // read as spam. Only a connection's provenance line survives.
        lede: null,
        driveId,
        root: [],
        app: null,
      };
    }
    const shared = places.sharedDrives.find((d) => d.driveId === driveId);
    if (shared) {
      return {
        kind: "drive",
        label: shared.name,
        name: shared.name,
        lede: null,
        driveId,
        root: [],
        app: shared.connector,
      };
    }
  }
  return {
    kind: "tree",
    label: "Documents",
    name: "Documents",
    lede: null,
    driveId: driveId ?? null,
    root: [],
    app: null,
  };
};

/** The connection whose root folder this is, in the given drive — or null
 *  for an ordinary folder. The drive is part of the test: a folder in
 *  another drive that shares a root's name is an ordinary folder. */
export const connectionRootOf = (
  places: Places | undefined,
  driveId: string | null,
  path: string[]
): { app: PlaceApp; connection: PlaceConnection } | null => {
  const hit = connectionAt(places, driveId, path);
  return hit !== null && hit.connection.path.length === path.length
    ? hit
    : null;
};

/** How a drive's listing names a connection's root folder (#12074): the app
 *  alone when the drive holds one place of that app, the place's own label
 *  when it holds several (two SharePoint sites in one drive) — rather than
 *  the stored folder's name, so "Sharepoint" and "Teams Channels" read as
 *  the app they are. Inside the drive the app's name is enough; the column
 *  and a Location cell, which stand outside it, name the place under its app
 *  (`connectionDisplayName`). Null for an ordinary folder. */
export const connectionRootName = (
  places: Places | undefined,
  driveId: string | null,
  path: string[]
): string | null => {
  const hit = connectionRootOf(places, driveId, path);
  if (hit === null) return null;
  const inDrive = hit.app.connections.filter(
    (connection) => (connection.driveId ?? null) === (driveId ?? null)
  );
  return inDrive.length > 1 ? hit.connection.label : hit.app.label;
};

/** A drive's folders with the connections' roots first (round 18g, Hunter:
 *  an organization with connectors inside its team drives sees SharePoint,
 *  Teams and OneNote at the top when it clicks into a drive), in the
 *  column's app order, then the ordinary folders as they came. Nothing is
 *  hidden: the connector's row in the column is a second door to the same
 *  place. */
export const pinConnectionRoots = <T extends { filePath: string[] }>(
  folders: readonly T[],
  places: Places | undefined,
  driveId: string | null
): T[] => {
  if (!places) return [...folders];
  const rank = new Map<string, number>();
  places.connectedApps.forEach((app, appIndex) =>
    app.connections.forEach((connection, index) =>
      rank.set(connection.key, appIndex * 1000 + index)
    )
  );
  const pinned: Array<{ folder: T; rank: number }> = [];
  const rest: T[] = [];
  for (const folder of folders) {
    const hit = connectionRootOf(places, driveId, folder.filePath);
    if (hit) pinned.push({ folder, rank: rank.get(hit.connection.key) ?? 0 });
    else rest.push(folder);
  }
  pinned.sort((a, b) => a.rank - b.rank);
  return [...pinned.map((p) => p.folder), ...rest];
};

/** A location as a trail from its place (round 19, Claude Design Q2): the
 *  place's name first, then the folders beneath it. At a place's root the
 *  trail is the place alone — "Root" is not a place. */
export const locationTrail = (
  places: Places | undefined,
  driveId: string | null,
  filePath: string[]
): string[] => {
  const place = placeFor(places, driveId, filePath);
  return [place.name, ...filePath.slice(place.root.length)];
};

/** The trail on one line: whole up to four crumbs, else the place, the
 *  top-level folder and the last folder with the middle elided. The
 *  top-level folder is the crumb ruling F (round 1) keeps because it is
 *  the most useful one; the place, constant across an organization
 *  without drives, is kept beside it rather than instead of it. */
export const locationLabel = (trail: string[]): string =>
  trail.length <= 4
    ? trail.join(" › ")
    : `${trail[0]} › ${trail[1]} › … › ${trail[trail.length - 1]}`;

/** A Recent folders card: where it opens. */
export interface FolderCard {
  driveId: string | null;
  path: string[];
}

/** What each of Home's folder cards is in, by card. A folder is in its
 *  parent, or at a place's root in the place itself, by the place's name
 *  (round 19, Claude Design Q2) — the parent's place, not the folder's own,
 *  so a connection's root folder is in its drive rather than in itself.
 *
 *  Cards that share a name are told apart instead (#12170): each names the
 *  nearest ancestor from which its trail down to the folder is its own
 *  among its namesakes, the way an editor labels same-named open files.
 *  Under a per-manager tree the parent ("Manager Documents") repeats under
 *  every vehicle, so three "Portfolio Reporting" cards would all read the
 *  same; they read their vehicles. The walk stays inside the card's place
 *  and compares the trail there, not the drive, so the same trail in two
 *  places falls through to the two place names.
 *
 *  Namesakes label at one level, the deepest any of them needed: a strip
 *  is read across, and three cards labelled at three depths of the tree
 *  read worse than three at the vehicle. The label is the ancestor at that
 *  level alone, unless another namesake's is the same ancestor (one
 *  vehicle, two document families beneath it): then each shows its whole
 *  trail from there, "Alpha Fund › Our Materials", so no two namesakes
 *  ever read the same. Only then — rendering the trail always would put
 *  the repeated structural folder back on every card. */
export const folderCardWithins = (
  places: Places | undefined,
  cards: readonly FolderCard[]
): string[] => {
  const fold = (segment: string): string => segment.toLowerCase();
  const trails = cards.map(({ driveId, path }) => {
    const parent = path.slice(0, -1);
    const place = placeFor(places, driveId, parent);
    return {
      name: fold(path[path.length - 1] ?? ""),
      place,
      inside: parent.slice(place.root.length),
    };
  });
  // The last `depth` folders of a trail: the parent, then its parent above.
  const tail = (inside: string[], depth: number): string[] =>
    inside.slice(Math.max(0, inside.length - depth));
  const key = (segments: string[]): string =>
    JSON.stringify(segments.map(fold));
  const namesakesOf = (index: number): number[] =>
    trails.flatMap((other, otherIndex) =>
      otherIndex !== index && other.name === trails[index]?.name
        ? [otherIndex]
        : []
    );
  // How far up each card has to go for its trail down to the folder to be
  // its own among its namesakes; null where the trail runs out inside the
  // place first.
  const depths = trails.map((card, index): number | null => {
    const namesakes = namesakesOf(index);
    for (let depth = 1; depth <= card.inside.length; depth++) {
      const own = key(tail(card.inside, depth));
      if (
        namesakes.every(
          (other) => key(tail(trails[other]?.inside ?? [], depth)) !== own
        )
      )
        return depth;
    }
    return null;
  });
  return trails.map((card, index) => {
    const depth = depths[index];
    if (depth === null || depth === undefined) return card.place.name;
    // Namesakes label at one level, the deepest any of them needed.
    const group = [index, ...namesakesOf(index)];
    const level = Math.max(...group.map((member) => depths[member] ?? 0));
    const trail = tail(card.inside, level);
    const head = trail[0] ?? "";
    const headCollides = namesakesOf(index).some((other) => {
      const otherDepth = depths[other];
      return (
        otherDepth !== null &&
        otherDepth !== undefined &&
        fold(tail(trails[other]?.inside ?? [], level)[0] ?? "") === fold(head)
      );
    });
    return headCollides ? trail.join(" › ") : head;
  });
};
